/**
 * 应付账单对账工作台 — 专属 API 服务
 *
 * 专为 APReconciliationWorkbench 页面设计。
 * 后端对应:
 *   - backend/api/v1/settlement/workbench_routes.py
 *
 * 两类 API:
 *   1. 数据查询 — IDS 列表、批次汇总 V2
 *   2. Pipeline 操作 — 启动、从阶段重试、停止（统一 API，前端不感知后端路由细节）
 *   3. 审核操作 — 确认、拒绝
 */

import apiClient from "../../../../api/axios";
import settlementRevisionApi from "../../../../api/settlementRevisionApi";
import apInvoicePipelineApi from "../../services/apInvoicePipelineApi";

const BASE = "/v1/settlement/ap/workbench";

const workbenchApi = {
    // =========================================================================
    // 1. 数据查询
    // =========================================================================

    /**
     * 获取工作台批次 IDS 列表
     *
     * 路径 A: 三元组精确匹配 (batch_number, vendor_id, standard_cost_type)
     */
    getIdsList: async (batchNumber, vendorId, standardCostType) => {
        const response = await apiClient.get(`${BASE}/ids-list`, {
            params: {
                batch_number: batchNumber,
                vendor_id: vendorId,
                standard_cost_type: standardCostType,
            },
        });
        return response.data;
    },

    /**
     * 获取工作台矩阵数据（旧链路）
     *
     * 历史接口：接收 eligibleIds，返回单源矩阵视图。
     * 当前工作台默认批次汇总已切到 getBatchSummaryV2。
     */
    getMatrixData: async (invoiceDataSourceIds, excludeSources = null) => {
        const payload = {
            invoice_data_source_ids: invoiceDataSourceIds,
        };
        if (excludeSources && excludeSources.length > 0) {
            payload.exclude_sources = excludeSources;
        }
        const response = await apiClient.post(`${BASE}/matrix-data`, payload);
        return response.data;
    },

    /**
     * 获取工作台批次汇总 V2 数据
     *
     * 新接口直接基于工作台三元组计算双源读模型，
     * 不再要求前端先拼接 eligibleIds。
     */
    getBatchSummaryV2: async (batchNumber, vendorId, standardCostType, excludeSources = null) => {
        const payload = {
            batch_number: batchNumber,
            vendor_id: vendorId,
            standard_cost_type: standardCostType,
        };
        if (excludeSources && excludeSources.length > 0) {
            payload.exclude_sources = excludeSources;
        }
        const response = await apiClient.post(`${BASE}/batch-summary-v2`, payload);
        return response.data;
    },

    // =========================================================================
    // 2. Pipeline 操作（统一 API — 前端只传 ids_id + stage_id，
    //    后端 Orchestrator 是状态转换和任务调度的唯一 source of truth）
    // =========================================================================

    /**
     * 启动 Pipeline（从 OCR 开始处理）
     *
     * @param {string} idsId - InvoiceDataSource UUID
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    startPipeline: apInvoicePipelineApi.startPipeline,

    /**
     * 从指定阶段重试 Pipeline
     *
     * 前端只需传 stage_id（如 "ocr", "parser", "allocation", "validation"），
     * 后端根据 stage_id 委托给 Orchestrator.retry_from_stage() 处理。
     *
     * @param {string} idsId - InvoiceDataSource UUID
     * @param {string} stageId - Pipeline 阶段 ID（与 pipeline.js 中的 stageId 一致）
     * @param {string} [extraPrompt] - 用户额外指令（可选）
     */
    retryFromStage: apInvoicePipelineApi.retryFromStage,

    /**
     * 批量 Pipeline 操作
     *
     * @param {string[]} idsIds - InvoiceDataSource UUID 列表
     * @param {string} action - "start" | "retry"
     * @param {string} stageId - 目标阶段
     */
    batchPipelineAction: apInvoicePipelineApi.batchPipelineAction,

    /**
     * 停止处理
     *
     * @param {string} idsId - InvoiceDataSource UUID
     */
    stopPipeline: apInvoicePipelineApi.stopPipeline,

    /**
     * 触发批次统筹验证
     *
     * 后端自行根据三元组查出所有 IDS，校验所有 IDS 是否都处于航班验证完毕之后的状态，
     * 然后触发批次验证。前端无需传 IDS 列表。
     *
     * @param {string} batchNumber - 批次号
     * @param {number} vendorId - 供应商 ID
     * @param {string} standardCostType - 标准费用类型
     */
    triggerBatchValidation: apInvoicePipelineApi.triggerBatchValidation,

    /**
     * 批量删除 IDS。后端逐条执行删除门禁：CONFIRMED 阻断，运行态先停止。
     *
     * @param {string[]} idsIds - InvoiceDataSource UUID 列表
     */
    batchDeleteInvoiceDataSources: async (idsIds) => {
        const response = await apiClient.delete("/v1/settlement/invoice-data-sources/batch", {
            data: { ids: idsIds },
        });
        return response.data;
    },

    // =========================================================================
    // 3. 单 IDS 审核数据聚合查询（新 API — 替代旧的 4 次串行调用）
    // =========================================================================

    /**
     * 获取单张账单的全量审核数据（聚合 API）
     *
     * 一次调用返回：IDS 基础信息 + ActionItem + 对账 payload 图层 + 校验结果。
     * 替代旧架构中的 4 次串行 API 调用：
     *   ① getInvoiceDataSourceById → IDS 状态
     *   ② getActionItems(search_key) → 搜索 ActionItem
     *   ③ getWorkbenchByActionItemId → 对账载荷
     *   ④ getValidationResult → 校验结果
     *
     * @param {string} idsId - InvoiceDataSource UUID
     * @returns {Promise<Object>} 聚合后的审核数据
     */
    getReviewData: async (idsId) => {
        const response = await apiClient.get(`${BASE}/review/${idsId}`);
        return response.data;
    },

    /**
     * 暂存审核修改。
     * 后端会重新解析当前权威 ActionItem，并校验前端持有的 action_item_id 是否仍然有效。
     */
    saveReviewPayload: async (idsId, actionItemId, userModifiedPayload) => {
        const response = await apiClient.patch(`${BASE}/review/${idsId}/payload`, {
            action_item_id: actionItemId,
            user_modified_payload: userModifiedPayload,
        });
        return response.data;
    },

    // =========================================================================
    // 4. 审核操作 (复用现有 API)
    // =========================================================================

    /**
     * 确认入库
     */
    commitInvoice: async (idsId, aaiId, options = {}) => {
        const response = await apiClient.post("/v1/settlement/ap/commit", {
            invoice_data_source_id: idsId,
            action_item_id: aaiId,
            ignore_warnings: options.ignoreWarnings,
        });
        return response.data;
    },

    /**
     * 批量确认入库
     */
    batchCommit: async (idsIds, ignoreWarnings = false) => {
        const response = await apiClient.post(`${BASE}/batch-commit`, {
            ids_ids: idsIds,
            ignore_warnings: ignoreWarnings,
        });
        return response.data;
    },

    /**
     * 批量设置结算币种
     */
    batchSetSettlementCurrency: async (idsIds, settlementCurrency) => {
        const response = await apiClient.post(`${BASE}/batch-settlement-currency`, {
            ids_ids: idsIds,
            settlement_currency: settlementCurrency,
        });
        return response.data;
    },

    /**
     * 拒绝
     */
    rejectInvoice: async (idsId, aaiId, reason) => {
        const response = await apiClient.post("/v1/settlement/ap/reject", {
            invoice_data_source_id: idsId,
            action_item_id: aaiId,
            reason,
        });
        return response.data;
    },

    // =========================================================================
    // 5. 作废与重发
    // =========================================================================

    assessRevisionImpact: async (targetId, targetType) => {
        const response = await settlementRevisionApi.assessImpact(targetId, targetType);
        return response.data;
    },

    voidInvoiceWithReparse: async (idsId, reason, mode = "reparse") => {
        const response = await settlementRevisionApi.voidInvoiceWithReparse(idsId, reason, mode);
        return response.data;
    },

    commitWithReplace: async (newIdsId) => {
        const response = await settlementRevisionApi.commitWithReplace(newIdsId);
        return response.data;
    },

    // =========================================================================
    // 6. 批次配置管理
    // =========================================================================

    /**
     * 批量修改批次属性（批次号/关联机场）
     *
     * 按三元组定位当前批次，批量更新所有 IDS 的 batch_number / airport_code。
     *
     * @param {Object} params
     * @param {number} params.vendorId - 供应商 ID
     * @param {string} params.standardCostType - 费用类型
     * @param {string} params.oldBatchNumber - 当前批次号
     * @param {string} [params.newBatchNumber] - 新批次号
     * @param {string} [params.newAirportCode] - 新关联机场
     */
    updateBatchConfig: async ({ vendorId, standardCostType, oldBatchNumber, newBatchNumber, newAirportCode }) => {
        const response = await apiClient.patch(`${BASE}/batch-config`, {
            vendor_id: vendorId,
            standard_cost_type: standardCostType,
            old_batch_number: oldBatchNumber,
            new_batch_number: newBatchNumber,
            new_airport_code: newAirportCode,
        });
        return response.data;
    },
};

export default workbenchApi;
